import { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Play, Pause, SkipBack, SkipForward,
  Volume2, VolumeX, Music2,
} from 'lucide-react';
import { useAudioPlayer } from '@/hooks/useAudioPlayer';
import { TRACKS, formatTime } from '@/lib/index';

// Animated waveform bars for the active track
function WaveformBars({ isPlaying }: { isPlaying: boolean }) {
  const bars = Array.from({ length: 28 }, (_, i) => i);
  return (
    <div className="flex items-end gap-[2px] h-8">
      {bars.map(i => (
        <motion.div
          key={i}
          className="w-[3px] rounded-full"
          style={{ background: 'var(--primary)' }}
          animate={
            isPlaying
              ? {
                  height: [
                    `${Math.random() * 50 + 20}%`,
                    `${Math.random() * 80 + 20}%`,
                    `${Math.random() * 40 + 10}%`,
                  ],
                }
              : { height: '25%' }
          }
          transition={
            isPlaying
              ? {
                  duration: 0.5 + Math.random() * 0.5,
                  repeat: Infinity,
                  repeatType: 'mirror',
                  ease: 'easeInOut',
                  delay: i * 0.03,
                }
              : { duration: 0.3 }
          }
        />
      ))}
    </div>
  );
}

// Slim progress bar / scrubber
function ProgressBar({
  progress,
  duration,
  onSeek,
}: {
  progress: number;
  duration: number;
  onSeek: (time: number) => void;
}) {
  const barRef = useRef<HTMLDivElement>(null);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect) return;
    const ratio = (e.clientX - rect.left) / rect.width;
    onSeek(ratio * duration);
  };

  return (
    <div
      ref={barRef}
      className="relative w-full h-1.5 bg-muted rounded-full cursor-pointer group"
      onClick={handleClick}
      role="slider"
      aria-valuenow={progress}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Playback progress"
    >
      <div
        className="absolute top-0 left-0 h-full rounded-full bg-primary transition-all duration-100"
        style={{ width: `${progress}%` }}
      />
      <div
        className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-primary opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
        style={{ left: `calc(${progress}% - 6px)` }}
      />
    </div>
  );
}

// Volume control
function VolumeControl({
  volume,
  isMuted,
  onVolumeChange,
  onToggleMute,
}: {
  volume: number;
  isMuted: boolean;
  onVolumeChange: (v: number) => void;
  onToggleMute: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={onToggleMute}
        className="p-1.5 rounded-lg hover:bg-muted/40 text-muted-foreground hover:text-foreground transition-colors"
        aria-label={isMuted ? 'Unmute' : 'Mute'}
      >
        {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      </button>
      <input
        type="range"
        min={0}
        max={1}
        step={0.01}
        value={isMuted ? 0 : volume}
        onChange={e => onVolumeChange(Number(e.target.value))}
        className="w-20 accent-primary cursor-pointer"
        aria-label="Volume"
      />
    </div>
  );
}

export function MusicSection() {
  const {
    currentTrack,
    currentTrackIndex,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    progress,
    togglePlay,
    selectTrack,
    playNext,
    playPrev,
    seek,
    changeVolume,
    toggleMute,
  } = useAudioPlayer();

  return (
    <section id="music" className="py-24 px-6 relative">
      {/* Section glow */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-px"
        style={{ background: 'linear-gradient(to right, transparent, var(--primary), transparent)' }}
      />

      <div className="max-w-5xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <p className="text-xs font-mono text-primary uppercase tracking-widest mb-3">Portfolio</p>
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Listen to My Work
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Original compositions and cinematic arrangements — select a track to listen.
          </p>
        </motion.div>

        {/* Player Card */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="rounded-2xl border border-border bg-card/60 backdrop-blur-md overflow-hidden shadow-2xl"
          style={{
            boxShadow: '0 25px 80px -15px oklch(0.65 0.18 180 / 0.15)',
          }}
        >
          {/* Now Playing Header */}
          <div className="px-6 pt-6 pb-4 border-b border-border">
            <div className="flex items-center gap-4">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{
                  background: 'linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)',
                }}
              >
                <Music2 className="w-7 h-7 text-primary-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentTrackIndex}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.25 }}
                  >
                    <p className="text-xs font-mono text-primary uppercase tracking-widest mb-0.5">
                      Now Playing
                    </p>
                    <h3 className="font-bold text-lg text-foreground truncate">
                      {currentTrack.title}
                    </h3>
                    <p className="text-sm text-muted-foreground truncate">
                      {currentTrack.description}
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>
              <div className="flex-shrink-0">
                <WaveformBars isPlaying={isPlaying} />
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="px-6 py-5">
            {/* Progress */}
            <div className="mb-4">
              <ProgressBar progress={progress} duration={duration} onSeek={seek} />
              <div className="flex justify-between mt-2 text-xs font-mono text-muted-foreground">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Transport + Volume */}
            <div className="flex items-center justify-between gap-4">
              <VolumeControl
                volume={volume}
                isMuted={isMuted}
                onVolumeChange={changeVolume}
                onToggleMute={toggleMute}
              />

              <div className="flex items-center gap-3">
                <button
                  onClick={playPrev}
                  className="p-2.5 rounded-xl hover:bg-muted/40 text-muted-foreground hover:text-foreground transition-all"
                  aria-label="Previous track"
                >
                  <SkipBack className="w-5 h-5" />
                </button>

                <motion.button
                  onClick={togglePlay}
                  whileTap={{ scale: 0.92 }}
                  className="w-14 h-14 rounded-full flex items-center justify-center font-semibold transition-all shadow-lg"
                  style={{
                    background: 'linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)',
                    color: 'var(--primary-foreground)',
                  }}
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                  {isPlaying
                    ? <Pause className="w-6 h-6" />
                    : <Play className="w-6 h-6 translate-x-0.5" />
                  }
                </motion.button>

                <button
                  onClick={playNext}
                  className="p-2.5 rounded-xl hover:bg-muted/40 text-muted-foreground hover:text-foreground transition-all"
                  aria-label="Next track"
                >
                  <SkipForward className="w-5 h-5" />
                </button>
              </div>

              <div className="w-32" />
            </div>
          </div>

          {/* Track List */}
          <div className="border-t border-border">
            {TRACKS.map((track, i) => (
              <motion.button
                key={track.id}
                onClick={() => selectTrack(i)}
                className={`w-full flex items-center gap-4 px-6 py-4 text-left transition-all duration-200 border-b border-border/50 last:border-b-0 ${
                  i === currentTrackIndex
                    ? 'bg-primary/10'
                    : 'hover:bg-muted/20'
                }`}
                whileHover={{ x: 4 }}
                transition={{ duration: 0.15 }}
              >
                {/* Track number / play icon */}
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-mono font-semibold"
                  style={{
                    background: i === currentTrackIndex ? 'var(--primary)' : 'var(--muted)',
                    color: i === currentTrackIndex ? 'var(--primary-foreground)' : 'var(--muted-foreground)',
                  }}
                >
                  {i === currentTrackIndex && isPlaying ? '♫' : i + 1}
                </div>

                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold truncate ${i === currentTrackIndex ? 'text-primary' : 'text-foreground'}`}>
                    {track.title}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{track.description}</p>
                </div>

                <span
                  className="text-xs font-mono px-2 py-0.5 rounded-full flex-shrink-0"
                  style={{
                    background: 'var(--muted)',
                    color: 'var(--muted-foreground)',
                  }}
                >
                  {track.genre}
                </span>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
